// scramble.js — Matrix-style text decode effect

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/**
 * Scramble an element's text, resolving to its final value.
 * @param {HTMLElement} el - Element with data-final attribute
 * @param {object} opts
 * @param {number} opts.duration - ms for full resolve
 * @param {number} opts.delay - ms before starting
 * @param {() => void} [opts.onComplete] - called once after the final text resolves
 */
export function scramble(el, { duration = 900, delay = 0, onComplete } = {}) {
  const finalText = el.dataset.final || el.textContent.trim();
  if (!finalText) return;

  let didComplete = false;

  function finish() {
    if (didComplete) return;
    didComplete = true;
    onComplete?.();
  }

  // Respect prefers-reduced-motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    el.textContent = finalText;
    finish();
    return;
  }

  let startTime = null;

  function tick(ts) {
    if (startTime === null) startTime = ts + delay;
    if (ts < startTime) {
      // Show scrambled text during delay
      el.textContent = randomString(finalText.length);
      requestAnimationFrame(tick);
      return;
    }

    const elapsed  = ts - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Resolve characters left-to-right based on progress
    const resolvedCount = Math.floor(
      // Ease in the resolution — slow at first, then rushes to finish
      easeInQuart(progress) * finalText.length
    );

    let display = '';
    for (let i = 0; i < finalText.length; i++) {
      if (i < resolvedCount) {
        display += finalText[i];
      } else if (finalText[i] === ' ') {
        display += ' ';
      } else {
        display += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
      }
    }

    el.textContent = display;

    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      el.textContent = finalText;
      finish();
    }
  }

  requestAnimationFrame(tick);
}

function randomString(length) {
  let s = '';
  for (let i = 0; i < length; i++) {
    s += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
  }
  return s;
}

function easeInQuart(t) {
  return t * t * t * t;
}
